import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { DropDownFillMode, DropDownRounded, DropDownSize } from '@progress/kendo-angular-dropdowns';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';

@Component({
  selector: 'image-capture-modal',
  templateUrl: './image-capture-modal.component.html',
  styleUrl: './image-capture-modal.component.scss',
  standalone: false,
})
export class ImageCaptureModalComponent implements AfterViewInit {
  @ViewChild('videoElement')
  public videoElement: ElementRef;

  @ViewChild('canvasElement')
  public canvasElement: ElementRef;

  public currentStream: MediaStream | undefined;
  public camerasArray: any[] = [];
  public activeCameraIndex: number;
  capturedImageFile: File;
  public isImageCaptured: boolean;
  public mirrorCameraState: boolean = false;

  public fillModeOutline: DropDownFillMode = 'flat';
  public roundedLarge: DropDownRounded = 'large';
  public sizeLarge: DropDownSize = 'large';

  constructor(
    private _activeModal: NgbActiveModal,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData,
    private _notificationService: NotificationService,
    private _translateService: TranslateService
  ) {}

  ngAfterViewInit(): void {
    this.setupDevices();
  }

  async setupDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return;
    }

    try {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'user',
          },
          audio: false,
        })
        .then(stream => {
          this.currentStream = stream;
          this.videoElement.nativeElement.srcObject = this.currentStream;
          this.videoElement.nativeElement.play();
          this.mirrorCamera(false);
        });

      if (!navigator.mediaDevices.enumerateDevices) {
        return;
      }

      await navigator.mediaDevices.enumerateDevices().then(devices => {
        const videoDevices = devices
          .filter(device => device.kind === 'videoinput')
          .map(videoDevice => {
            return {
              deviceId: videoDevice.deviceId,
              deviceLabel: videoDevice.label,
            };
          });

        this.activeCameraIndex = 0;
        this.camerasArray = videoDevices;
      });
    } catch (e) {
      console.log(e);
    }
  }

  stopMediaStream(stream: MediaStream | undefined) {
    if (typeof stream === 'undefined') {
      return;
    }
    stream.getTracks().forEach(track => {
      track.stop();
    });
  }

  mirrorCamera(state: boolean) {
    this.mirrorCameraState = state;
  }

  async switchCamera(deviceId: string) {
    if (
      this.camerasArray[this.activeCameraIndex].deviceId === deviceId ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      return;
    }

    try {
      this.stopMediaStream(this.currentStream);

      const constraints = {
        video: { deviceId: { exact: deviceId } },
        audio: false,
      };

      await navigator.mediaDevices.getUserMedia(constraints).then(stream => {
        this.currentStream = stream;
        this.videoElement.nativeElement.srcObject = this.currentStream;
        this.videoElement.nativeElement.play();

        this.activeCameraIndex = this.camerasArray.findIndex(x => x.deviceId === deviceId);
        this.mirrorCamera(false);
      });
    } catch (e) {
      console.log(e);
    }
  }

  onActiveWebcamChange(e: any | undefined = undefined) {
    const isDualCamera: boolean = typeof e === 'undefined' && this.camerasArray.length === 2;
    let device = isDualCamera ? this.camerasArray[Math.abs(this.activeCameraIndex - 1)] : e;
    this.switchCamera(device.deviceId);
  }

  drawImageToCanvas(image: any) {
    let canvasWidth = this.videoElement.nativeElement.videoWidth;
    let canvasHeight = this.videoElement.nativeElement.videoHeight;

    this.canvasElement.nativeElement.width = canvasWidth;
    this.canvasElement.nativeElement.height = canvasHeight;

    let canvasContext = this.canvasElement.nativeElement.getContext('2d');

    canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);
    canvasContext.save();
    canvasContext.scale(this.mirrorCameraState ? -1 : 1, 1);
    canvasContext.drawImage(image, this.mirrorCameraState ? canvasWidth * -1 : 0, 0, canvasWidth, canvasHeight);
    canvasContext.restore();
  }

  imageCapture() {
    this.drawImageToCanvas(this.videoElement.nativeElement);
    const capturedImage = this.canvasElement.nativeElement.toDataURL('image/png');
    this.dataUrlToFile(capturedImage, 'capture-image.png').then(file => {
      this.capturedImageFile = file;
      const allowedFileSizeinBytes = this._envConfig.ALLOWED_FILE_SIZE * 1000000;
      if (file.size > allowedFileSizeinBytes) {
        this._notificationService.showWarning(
          this._translateService.instant('common.fileSizeExceeded', { maxSize: this._envConfig.ALLOWED_FILE_SIZE }),
          this._translateService.instant('common.warning')
        );
        return;
      } else {
        this.isImageCaptured = true;
      }
    });
  }

  repeatImageCapture() {
    this.isImageCaptured = false;
  }

  async dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
    const [meta, base64] = dataUrl.split(',');
    const mime = /data:(.*?);base64/.exec(meta)?.[1] ?? 'application/octet-stream';

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: mime });
  }

  closeModalWithImageData(data: File | undefined = undefined) {
    this._activeModal.close(data);

    this.stopMediaStream(this.currentStream);
  }

  closeModal(status: boolean = false) {
    this._activeModal.close(status);
    this.stopMediaStream(this.currentStream);
  }
}
